import { useMemo, useState } from "react";

import { useArtifactReviews, useArtifacts, useCreateRun, useUpdateArtifact } from "@/api/queries";
import type { Artifact, Json } from "@/api/types";
import { ArtifactDetail } from "@/components/ArtifactDetail";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { Modal } from "@/components/Modal";
import { RemoteState } from "@/components/RemoteState";
import { Tag } from "@/components/Tag";
import { useUiStore } from "@/store/ui";

/** @dataSource remote — 分场卡来自项目内 `scene` Artifact，审阅记录来自 Artifact 评审 */

interface Scene {
  id: string;
  code: string;
  title: string;
  duration: string;
  status: string;
  slug: string;
  action: string;
  character: string;
  dialogue: string;
  choice: string;
}

function asString(value: Json | undefined, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function readScenes(artifact: Artifact | undefined): Scene[] {
  const raw = artifact?.content?.scenes;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is Record<string, Json> =>
        typeof item === "object" && item !== null && !Array.isArray(item),
    )
    .map((item) => ({
      id: asString(item.id),
      code: asString(item.code, asString(item.id)),
      title: asString(item.title),
      duration: asString(item.duration, "—"),
      status: asString(item.status, "draft"),
      slug: asString(item.slug),
      action: asString(item.action),
      character: asString(item.character),
      dialogue: asString(item.dialogue),
      choice: asString(item.choice),
    }))
    .filter((item) => item.id);
}

/** 正式脚本工作台：分场列表 + 正文 + 审阅记录，全部来自后端。 */
export function ScriptView() {
  const projectId = useUiStore((state) => state.projectId);
  const selectedSceneId = useUiStore((state) => state.selectedSceneId);
  const selectScene = useUiStore((state) => state.selectScene);
  const pushToast = useUiStore((state) => state.pushToast);

  const artifactsQuery = useArtifacts(projectId);
  const [aiOpen, setAiOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const sceneArtifact = useMemo(
    () =>
      (artifactsQuery.data ?? [])
        .filter((item) => item.type === "scene")
        .sort((a, b) => b.version - a.version)[0],
    [artifactsQuery.data],
  );

  const scenes = useMemo(() => readScenes(sceneArtifact), [sceneArtifact]);
  const scene = scenes.find((item) => item.id === selectedSceneId) ?? scenes[0];

  const reviewsQuery = useArtifactReviews(sceneArtifact?.id ?? null);
  const updateArtifact = useUpdateArtifact(sceneArtifact?.id ?? "");
  const createRun = useCreateRun(projectId);

  const totalDuration = scenes.reduce((sum, item) => {
    const match = /(\d+):(\d+)/.exec(item.duration);
    return match ? sum + Number(match[1]) * 60 + Number(match[2]) : sum;
  }, 0);

  return (
    <RemoteState queries={[artifactsQuery]} title="脚本加载失败">
      <div className="script-layout">
        <aside className="script-scenes">
          <header className="column-heading">
            <h2>{sceneArtifact?.title ?? "分场"}</h2>
            <p>
              {scenes.length} 场 · {Math.floor(totalDuration / 60)}分
              {String(totalDuration % 60).padStart(2, "0")}秒 · v{sceneArtifact?.version ?? "—"}
            </p>
          </header>
          {scenes.length === 0 ? (
            <EmptyState
              title="还没有分场卡"
              description="分场由 scene Artifact 定义，可在生成中心用「编剧助理」产出。"
            />
          ) : (
            <ul className="scene-list">
              {scenes.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`scene-item ${item.id === scene?.id ? "is-active" : ""}`}
                    aria-pressed={item.id === scene?.id}
                    onClick={() => selectScene(item.id)}
                  >
                    <small>
                      {item.code} · {item.duration}
                    </small>
                    <strong>{item.title}</strong>
                    <span>{item.status}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="script-editor">
          {scene ? (
            <>
              <div className="editor-toolbar">
                <div className="meta-row">
                  <Tag tone="gold">{scene.status}</Tag>
                  <Tag>v{sceneArtifact?.version ?? "—"}</Tag>
                  {sceneArtifact?.status === "draft" ? <Tag tone="red">草稿</Tag> : null}
                </div>
                <div className="toolbar-right">
                  <Button
                    size="small"
                    variant="quiet"
                    id="artifact-detail"
                    disabled={!sceneArtifact}
                    onClick={() => setDetailId(sceneArtifact?.id ?? null)}
                  >
                    产物详情
                  </Button>
                  <Button
                    size="small"
                    variant="quiet"
                    id="ai-candidate"
                    disabled={!sceneArtifact}
                    onClick={() => setAiOpen(true)}
                  >
                    AI候选
                  </Button>
                  <Button
                    size="small"
                    variant="primary"
                    id="save-script"
                    disabled={!sceneArtifact || updateArtifact.isPending}
                    onClick={() => {
                      if (!sceneArtifact) return;
                      updateArtifact.mutate(
                        {
                          // Scene 是结构化对象数组，Json 的联合类型无法静态收窄，
                          // 这里在边界显式断言：payload 由后端的 JSON Schema 校验。
                          patch: { scenes } as unknown as Record<string, Json>,
                        },
                        {
                          onSuccess: () =>
                            pushToast({
                              title: "已创建新版本",
                              detail: "已批准版本不被原地改写，修改会生成新版本待审阅",
                              mark: "✓",
                            }),
                          onError: (error) =>
                            pushToast({
                              title: "保存失败",
                              detail: error instanceof Error ? error.message : "未知错误",
                              mark: "!",
                            }),
                        },
                      );
                    }}
                  >
                    {updateArtifact.isPending ? "保存中…" : "保存修改"}
                  </Button>
                </div>
              </div>
              <article className="editor-page">
                <p className="script-slug">{scene.slug}</p>
                <div
                  id="script-content"
                  role="textbox"
                  aria-multiline="true"
                  aria-label="脚本正文"
                  tabIndex={-1}
                >
                  <p className="script-action">{scene.action}</p>
                  <p className="script-character">{scene.character}</p>
                  <p className="script-dialogue">{scene.dialogue}</p>
                  {scene.choice ? <div className="script-choice">{scene.choice}</div> : null}
                </div>
              </article>
            </>
          ) : (
            <EmptyState title="未选中分场" />
          )}
        </section>

        <aside className="script-notes">
          <header className="column-heading">
            <h3>审阅记录</h3>
            <p>
              {(reviewsQuery.data ?? []).length} 条 · Artifact v{sceneArtifact?.version ?? "—"}
            </p>
          </header>
          <div className="notes-body">
            {(reviewsQuery.data ?? []).length === 0 ? (
              <EmptyState title="暂无审阅记录" />
            ) : (
              <ul className="comment-list">
                {(reviewsQuery.data ?? []).map((review) => (
                  <li className="comment-item" key={review.id}>
                    <span className="avatar avatar-writer">{review.actor_role.slice(0, 1)}</span>
                    <div>
                      <p>{review.comment || review.decision}</p>
                      <small>
                        {review.actor_id} · {review.decision} · {review.created_at ?? ""}
                      </small>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        <Modal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          eyebrow="AI 候选"
          title={`${scene?.code ?? ""} 对白候选`}
          footer={
            <>
              <Button variant="quiet" onClick={() => setAiOpen(false)}>
                关闭
              </Button>
              <Button
                variant="primary"
                disabled={createRun.isPending || !scene}
                onClick={() => {
                  if (!scene) return;
                  createRun.mutate(
                    {
                      capability: "actor",
                      task: `为分场 ${scene.code}「${scene.title}」给出一版表演变体候选`,
                      writeTarget: "script",
                      title: `${scene.code} 表演变体候选`,
                      idempotencyKey: `actor-${scene.id}-${Date.now().toString(36)}`,
                    },
                    {
                      onSuccess: () => {
                        setAiOpen(false);
                        pushToast({
                          title: "候选已生成",
                          detail: "候选以 draft 写入，审查后可采用",
                          mark: "✓",
                        });
                      },
                    },
                  );
                }}
              >
                {createRun.isPending ? "生成中…" : "生成候选"}
              </Button>
            </>
          }
        >
          {createRun.isError ? (
            <p className="inline-note">{(createRun.error as Error)?.message ?? "生成失败"}</p>
          ) : createRun.data ? (
            <div className="choice-list">
              {Array.isArray(createRun.data.outputs.candidates) &&
              createRun.data.outputs.candidates.length ? (
                createRun.data.outputs.candidates.map((candidate, index) => (
                  <div className="choice-item" key={index}>
                    <strong>{index + 1}</strong>
                    <span>{JSON.stringify(candidate)}</span>
                  </div>
                ))
              ) : (
                <p className="inline-note">
                  运行已完成，未返回候选内容。状态：{createRun.data.status}
                </p>
              )}
            </div>
          ) : (
            <p className="inline-note">
              将调用「角色演员」能力模块，产出写入 script 类型的候选 Artifact。
            </p>
          )}
        </Modal>

        <ArtifactDetail artifactId={detailId} onClose={() => setDetailId(null)} />
      </div>
    </RemoteState>
  );
}
